import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/design-system/components/react";
import { useAuth } from "@/context/AuthContext";
import { useDevicePanelStudies, usePrefetchDevicePanelStudy } from "@/hooks/useDevicePanel";
import { worklistSteps, type WorklistSteps } from "@/types/notificationView";
import { panelCaseUrl, type DevicePanelStudyRow } from "@/types/devicePanel";

/**
 * Stepping from one study to the next in its panel's worklist order, over the
 * studies `counted` admits (all by default): where the study sits, ← / → to the
 * neighbours, and both neighbours loaded ahead so a step opens on cached output.
 * Null until the worklist loads, or while `enabled` is off.
 */
export function useCaseSteps(
  panelId: string,
  studyUid: string,
  {
    counted,
    enabled = true,
  }: { counted?: (row: DevicePanelStudyRow) => boolean; enabled?: boolean } = {},
): WorklistSteps | null {
  const { token } = useAuth();
  const navigate = useNavigate();
  const list = useDevicePanelStudies(token, enabled ? panelId : undefined);
  const prefetch = usePrefetchDevicePanelStudy(token, panelId);
  const steps = useMemo(
    () => (enabled && list.data ? worklistSteps(list.data.studies, studyUid, counted) : null),
    [enabled, list.data, studyUid, counted],
  );

  useEffect(() => {
    for (const neighbour of [steps?.previous, steps?.next])
      if (neighbour) prefetch(neighbour.study_uid);
  }, [steps, prefetch]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target =
        event.key === "ArrowLeft"
          ? steps?.previous
          : event.key === "ArrowRight"
            ? steps?.next
            : null;
      if (!target) return;
      event.preventDefault();
      navigate(panelCaseUrl(panelId, target.study_uid));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps, navigate, panelId]);

  return steps;
}

/** Where the study sits, e.g. "Case 3 of 38", or how many there are when the
 * study itself is not counted. */
export function StepCounter({ steps, noun }: { steps: WorklistSteps; noun: string }) {
  return (
    <span className="font-mono text-[11px] text-fg-muted tabular-nums">
      {steps.position === null
        ? `${steps.total} ${noun}s`
        : `${noun[0].toUpperCase()}${noun.slice(1)} ${steps.position} of ${steps.total}`}
    </span>
  );
}

/** Previous and Next, each disabled at its end of the worklist. */
export function StepButtons({
  panelId,
  steps,
  noun,
  className,
}: {
  panelId: string;
  steps: WorklistSteps | null;
  noun: string;
  className?: string;
}) {
  const navigate = useNavigate();
  return (
    <>
      {(
        [
          ["‹ Prev", `Previous ${noun}`, "ArrowLeft", steps?.previous],
          ["Next ›", `Next ${noun}`, "ArrowRight", steps?.next],
        ] as const
      ).map(([label, name, key, target]) => (
        <Button
          key={key}
          type="button"
          variant="outline"
          size="sm"
          aria-label={name}
          aria-keyshortcuts={key}
          disabled={!target}
          onClick={() => target && navigate(panelCaseUrl(panelId, target.study_uid))}
          className={className}
        >
          {label}
        </Button>
      ))}
    </>
  );
}
